import React, { useState, useEffect } from 'react';
import { referralAPI } from '../lib/api';
import {
  Wallet, ArrowDownLeft, ArrowUpRight, DollarSign, Clock,
  Check, X, Send, CreditCard, Save, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const ReferralWalletPage = () => {
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [minPayoutInr, setMinPayoutInr] = useState(1000);
  const [minPayoutUsd, setMinPayoutUsd] = useState(50);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutSettings, setPayoutSettings] = useState({ upi_id: '', paypal_email: '' });
  const [activeTab, setActiveTab] = useState('balance');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [walletRes, settingsRes] = await Promise.all([
        referralAPI.getWallet(),
        referralAPI.getPayoutSettings(),
      ]);
      setWalletData(walletRes.data.wallet);
      setTransactions(walletRes.data.transactions || []);
      setPayouts(walletRes.data.payouts || []);
      setMinPayoutInr(walletRes.data.min_payout_inr || 1000);
      setMinPayoutUsd(walletRes.data.min_payout_usd || 50);
      setPayoutSettings(settingsRes.data || { upi_id: '', paypal_email: '' });
    } catch { toast.error('Failed to load wallet'); }
    finally { setLoading(false); }
  };

  const handleSavePayoutSettings = async () => {
    try {
      await referralAPI.updatePayoutSettings(payoutSettings);
      toast.success('Payout settings saved');
    } catch { toast.error('Failed to save payout settings'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  const w = walletData || {};

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#1E8E3E] to-[#137333] rounded-xl flex items-center justify-center">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Wallet & Payouts</h1>
            <p className="text-[#5F6368]">Manage your referral earnings and request payouts</p>
          </div>
        </div>
        <Link to="/dashboard/referrals" className="btn-outline flex items-center gap-2 text-sm" data-testid="back-to-referrals-btn">
          Back to Referrals
        </Link>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="wallet-balances">
        <div className="dashboard-card bg-gradient-to-br from-[#E8F0FE] to-white">
          <p className="text-xs text-[#5F6368] mb-1">Available Balance</p>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-[#1A73E8]">₹{(w.balance_inr || 0).toFixed(2)}</p>
            <p className="text-lg font-semibold text-[#1A73E8]/70">${(w.balance_usd || 0).toFixed(2)}</p>
          </div>
          {((w.balance_inr || 0) >= minPayoutInr || (w.balance_usd || 0) >= minPayoutUsd) && (
            <button onClick={() => setShowPayoutModal(true)} className="mt-3 btn-google text-sm w-full flex items-center justify-center gap-2" data-testid="request-payout-btn">
              <Send className="w-4 h-4" /> Request Payout
            </button>
          )}
        </div>
        <div className="dashboard-card">
          <p className="text-xs text-[#5F6368] mb-1">Pending (Awaiting Approval)</p>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-[#F9AB00]">₹{(w.pending_inr || 0).toFixed(2)}</p>
            <p className="text-lg font-semibold text-[#F9AB00]/70">${(w.pending_usd || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="dashboard-card">
          <p className="text-xs text-[#5F6368] mb-1">Total Earned (Lifetime)</p>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-[#1E8E3E]">₹{(w.total_earned_inr || 0).toFixed(2)}</p>
            <p className="text-lg font-semibold text-[#1E8E3E]/70">${(w.total_earned_usd || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Min Payout Info */}
      <div className="bg-[#E8F0FE] rounded-lg p-3 text-sm text-[#1A73E8] flex items-center gap-2">
        <DollarSign className="w-4 h-4 flex-shrink-0" />
        Minimum payout: ₹{minPayoutInr} / ${minPayoutUsd}. Balance must meet threshold to request a payout.
      </div>

      {/* Tabs */}
      <div className="dashboard-card">
        <div className="flex gap-1 bg-[#F1F3F4] rounded-lg p-1 mb-4">
          {['balance', 'payouts', 'settings'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'bg-white text-[#202124] shadow-sm' : 'text-[#5F6368]'}`}
              data-testid={`wallet-tab-${tab}`}
            >{tab === 'balance' ? 'Transactions' : tab === 'payouts' ? 'Payout History' : 'Payout Settings'}</button>
          ))}
        </div>

        {activeTab === 'balance' && (
          transactions.length === 0 ? (
            <div className="text-center py-8">
              <ArrowDownLeft className="w-10 h-10 text-[#DADCE0] mx-auto mb-2" />
              <p className="text-sm text-[#5F6368]">No transactions yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Description</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Amount</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(t => (
                    <tr key={t.id} className="border-b border-[#F1F3F4]">
                      <td className="py-2.5 px-3 text-[#202124]">{t.description}</td>
                      <td className={`py-2.5 px-3 font-medium ${t.amount >= 0 ? 'text-[#1E8E3E]' : 'text-[#D93025]'}`}>
                        {t.amount >= 0 ? '+' : ''}{t.currency === 'INR' ? '₹' : '$'}{Math.abs(t.amount).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          t.status === 'completed' ? 'bg-[#E6F4EA] text-[#1E8E3E]' :
                          t.status === 'rejected' ? 'bg-[#FCE8E6] text-[#D93025]' :
                          'bg-[#FEF7E0] text-[#F9AB00]'
                        }`}>{t.status}</span>
                      </td>
                      <td className="py-2.5 px-3 text-[#5F6368]">{new Date(t.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {activeTab === 'payouts' && (
          payouts.length === 0 ? (
            <div className="text-center py-8">
              <Send className="w-10 h-10 text-[#DADCE0] mx-auto mb-2" />
              <p className="text-sm text-[#5F6368]">No payout requests yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payouts-table">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Amount</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Method</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map(p => (
                    <tr key={p.id} className="border-b border-[#F1F3F4]">
                      <td className="py-2.5 px-3 font-medium">{p.currency === 'INR' ? '₹' : '$'}{p.amount?.toFixed(2)}</td>
                      <td className="py-2.5 px-3 uppercase text-xs">{p.method}</td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          p.status === 'paid' ? 'bg-[#E6F4EA] text-[#1E8E3E]' :
                          p.status === 'rejected' ? 'bg-[#FCE8E6] text-[#D93025]' :
                          'bg-[#FEF7E0] text-[#F9AB00]'
                        }`}>{p.status}</span>
                      </td>
                      <td className="py-2.5 px-3 text-[#5F6368]">{new Date(p.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {activeTab === 'settings' && (
          <div className="space-y-4" data-testid="payout-settings-form">
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-1">
                <IndianRupee className="w-3.5 h-3.5 inline mr-1" />UPI ID
              </label>
              <input
                value={payoutSettings.upi_id || ''}
                onChange={e => setPayoutSettings({ ...payoutSettings, upi_id: e.target.value })}
                className="input-google"
                placeholder="yourname@upi"
                data-testid="upi-id-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-1">
                <CreditCard className="w-3.5 h-3.5 inline mr-1" />PayPal Email
              </label>
              <input
                value={payoutSettings.paypal_email || ''}
                onChange={e => setPayoutSettings({ ...payoutSettings, paypal_email: e.target.value })}
                className="input-google"
                placeholder="you@example.com"
                data-testid="paypal-email-input"
              />
            </div>
            <button onClick={handleSavePayoutSettings} className="btn-google flex items-center gap-2" data-testid="save-payout-settings-btn">
              <Save className="w-4 h-4" /> Save Payout Settings
            </button>
          </div>
        )}
      </div>

      {/* Payout Request Modal */}
      {showPayoutModal && (
        <PayoutModal
          wallet={w}
          minPayoutInr={minPayoutInr}
          minPayoutUsd={minPayoutUsd}
          payoutSettings={payoutSettings}
          onClose={() => setShowPayoutModal(false)}
          onSuccess={() => { setShowPayoutModal(false); fetchAll(); }}
        />
      )}
    </div>
  );
};

const PayoutModal = ({ wallet, minPayoutInr, minPayoutUsd, payoutSettings, onClose, onSuccess }) => {
  const [currency, setCurrency] = useState(wallet.balance_inr >= minPayoutInr ? 'INR' : 'USD');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi');
  const [upiId, setUpiId] = useState(payoutSettings.upi_id || '');
  const [paypalEmail, setPaypalEmail] = useState(payoutSettings.paypal_email || '');
  const [sending, setSending] = useState(false);

  const available = currency === 'INR' ? wallet.balance_inr : wallet.balance_usd;
  const minPayout = currency === 'INR' ? minPayoutInr : minPayoutUsd;
  const sym = currency === 'INR' ? '₹' : '$';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt < minPayout) {
      toast.error(`Minimum payout is ${sym}${minPayout}`);
      return;
    }
    if (amt > available) {
      toast.error(`Insufficient balance. Available: ${sym}${available.toFixed(2)}`);
      return;
    }
    setSending(true);
    try {
      await referralAPI.requestPayout({
        amount: amt,
        currency,
        method,
        upi_id: method === 'upi' ? upiId : null,
        paypal_email: method === 'paypal' ? paypalEmail : null,
      });
      toast.success('Payout request submitted!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request payout');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="payout-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="payout-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#DADCE0]">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            <Send className="w-5 h-5 inline mr-2 text-[#1A73E8]" />Request Payout
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F1F3F4] flex items-center justify-center" data-testid="payout-modal-close">
            <X className="w-4 h-4 text-[#5F6368]" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-2">Currency</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setCurrency('INR')}
                className={`p-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${currency === 'INR' ? 'border-[#1A73E8] bg-[#E8F0FE] text-[#1A73E8]' : 'border-[#DADCE0] text-[#5F6368]'}`}
                data-testid="currency-inr-btn"
              >₹ INR (Available: ₹{wallet.balance_inr?.toFixed(2)})</button>
              <button type="button" onClick={() => setCurrency('USD')}
                className={`p-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${currency === 'USD' ? 'border-[#1A73E8] bg-[#E8F0FE] text-[#1A73E8]' : 'border-[#DADCE0] text-[#5F6368]'}`}
                data-testid="currency-usd-btn"
              >$ USD (Available: ${wallet.balance_usd?.toFixed(2)})</button>
            </div>
          </div>
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-1">Amount ({sym})</label>
            <input type="number" step="0.01" min={minPayout} max={available} value={amount} onChange={e => setAmount(e.target.value)} className="input-google" placeholder={`Min: ${sym}${minPayout}`} required data-testid="payout-amount-input" />
          </div>
          {/* Method */}
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-2">Payout Method</label>
            <div className="grid grid-cols-3 gap-2">
              {['upi', 'paypal', 'wallet'].map(m => (
                <button key={m} type="button" onClick={() => setMethod(m)}
                  className={`p-2.5 rounded-lg border-2 text-xs font-medium transition-colors uppercase ${method === m ? 'border-[#1A73E8] bg-[#E8F0FE] text-[#1A73E8]' : 'border-[#DADCE0] text-[#5F6368]'}`}
                  data-testid={`method-${m}-btn`}
                >{m}</button>
              ))}
            </div>
          </div>
          {/* UPI */}
          {method === 'upi' && (
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-1">UPI ID</label>
              <input value={upiId} onChange={e => setUpiId(e.target.value)} className="input-google" placeholder="yourname@upi" required data-testid="payout-upi-input" />
            </div>
          )}
          {/* PayPal */}
          {method === 'paypal' && (
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-1">PayPal Email</label>
              <input type="email" value={paypalEmail} onChange={e => setPaypalEmail(e.target.value)} className="input-google" placeholder="you@example.com" required data-testid="payout-paypal-input" />
            </div>
          )}
          {method === 'wallet' && (
            <div className="bg-[#E8F0FE] p-3 rounded-lg text-xs text-[#1A73E8]">
              Amount will remain in your wallet balance for future use.
            </div>
          )}
          <button type="submit" disabled={sending} className="w-full btn-google flex items-center justify-center gap-2" data-testid="submit-payout-btn">
            {sending ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Processing...</> : <><Send className="w-4 h-4" /> Request Payout</>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReferralWalletPage;
